// Client-owned projection of services/api/app/services/execution_context.py v1.
// Authority, resolved language, identity and transitions belong to API admission.
export type ExecutionHints = {
  project_id?: string
  period_id?: string
  selected_file_ids?: string[]
  target_hint?: string
  application_locale?: string
  reply_language?: string
  report_output_language?: string
}

export type ExecutionContextInput = {
  task?: string
  requested_outputs?: string[]
  hints?: ExecutionHints
}

export type ExecutionSelector = {
  version: 1
  transition: "new" | "steer" | "resume"
  target_input_id?: string
}

export type ExecutionIntent = { transition: "new" } | { transition: "steer" | "resume"; messageID?: string }

export type ExecutionBinding = {
  contract: "andmera.execution-binding.v1"
  runtime_session_id: string
  message_id: string
  root_input_id: string
  current_input_id: string
  previous_input_id?: string
  transition: ExecutionSelector["transition"]
  phase: "admitted" | "promoted"
  input_sequence: number
  turn_id?: string
  can_steer: boolean
  input: { text: string }
}

// A code, never visible copy. Submission boundaries translate the existing error key.
export class ExecutionBindingError extends Error {
  constructor() {
    super("execution_binding_unavailable")
  }
}

export function knownExecutionMessage(messages: readonly { id: string; type: string }[]) {
  // Queued/optimistic users are not evidence of the active request. The server
  // checks whether this observed assistant still belongs to the live lease.
  return messages.findLast((message) => message.type === "assistant")?.id
}

export async function resolveExecutionSelector(
  sessionID: string,
  intent: ExecutionIntent,
  lookup: (input: { sessionID: string; messageID: string }) => Promise<{ data?: ExecutionBinding }>,
): Promise<ExecutionSelector> {
  if (intent.transition === "new") return { version: 1, transition: "new" }
  if (!intent.messageID) throw new ExecutionBindingError()
  const { data } = await lookup({ sessionID, messageID: intent.messageID })
  if (
    !data || data.contract !== "andmera.execution-binding.v1" ||
    data.runtime_session_id !== sessionID || data.message_id !== intent.messageID ||
    typeof data.root_input_id !== "string" || !data.root_input_id ||
    (intent.transition === "steer" && data.can_steer !== true)
  ) throw new ExecutionBindingError()
  return { version: 1, transition: intent.transition, target_input_id: data.root_input_id }
}

export const executionContextPrefix = "execution_context: "

export function coworkExecutionContext(text: string, input: ExecutionContextInput = {}) {
  const hints = input.hints ?? {}
  return {
    version: 1 as const,
    intent: { task: input.task ?? "chat", text, requested_outputs: [...(input.requested_outputs ?? [])] },
    hints: {
      ...(hints.project_id !== undefined ? { project_id: hints.project_id } : {}),
      ...(hints.period_id !== undefined ? { period_id: hints.period_id } : {}),
      ...(hints.selected_file_ids !== undefined ? { selected_file_ids: [...hints.selected_file_ids] } : {}),
      ...(hints.target_hint !== undefined ? { target_hint: hints.target_hint } : {}),
      ...(hints.application_locale !== undefined ? { application_locale: hints.application_locale } : {}),
      ...(hints.reply_language !== undefined ? { reply_language: hints.reply_language } : {}),
      ...(hints.report_output_language !== undefined ? { report_output_language: hints.report_output_language } : {}),
    },
  }
}
